import { psmGet, psmPost, psmPut, psmPatch } from "../api/api";

/**
 * The shared request helpers in ../api/api return the full parsed response
 * body (e.g. { data, message, ... }). The tenant registry UI expects just
 * the `data` payload, so we unwrap it here rather than changing every call
 * site (mirrors the same unwrapData() convention used in attendanceService).
 */
function unwrapData(response) {
  return response && response.data !== undefined ? response.data : response;
}

/** GET /tenant-registry/search — X-Service-Code: PSM01S */
export async function getTenants({ page = 0, size = 10, sortBy = "tenantName", sortDir = "ASC" } = {}) {
  const response = await psmGet(
    `/tenant-registry/search?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`,
    "PSM01S"
  );
  return unwrapData(response); // Page object: { content, totalElements, ... }
}

/** GET /tenant-registry/{tenantId} — X-Service-Code: PSM01A */
export async function getTenant(tenantId) {
  const response = await psmGet(`/tenant-registry/${tenantId}`, "PSM01A");
  return unwrapData(response);
}

/** GET /tenant-registry/search/name — X-Service-Code: PSM01S */
export async function searchTenantsByName(name, { page = 0, size = 10, sortBy = "tenantName", sortDir = "ASC" } = {}) {
  const response = await psmGet(
    `/tenant-registry/search/name?name=${encodeURIComponent(name)}&page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`,
    "PSM01S"
  );
  return unwrapData(response); // Page object — same shape as getTenants()
}

/** GET /tenant-registry/search/expiring-subscriptions — X-Service-Code: PSM01A */
export async function getExpiringTenants(daysAhead = 30) {
  const threshold = new Date();
  threshold.setDate(threshold.getDate() + daysAhead);
  const date = threshold.toISOString().slice(0, 10);
  const response = await psmGet(`/tenant-registry/search/expiring-subscriptions?date=${date}`, "PSM01A");
  return unwrapData(response); // plain array
}

/** GET /tenant-registry/search/active-status — X-Service-Code: PSM01A */
export async function getTenantsByActiveStatus(active, { page = 0, size = 10, sortBy = "tenantName", sortDir = "ASC" } = {}) {
  const response = await psmGet(
    `/tenant-registry/search/active-status?active=${active}&page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`,
    "PSM01A"
  );
  return unwrapData(response); // Page object
}

/** GET /tenant-registry/count — X-Service-Code: PSM01A */
export async function getTenantCount() {
  const response = await psmGet("/tenant-registry/count", "PSM01A");
  return unwrapData(response);
}

/** GET /tenant-registry/count/active-status — X-Service-Code: PSM01A (active is a required query param) */
export async function getTenantCountByActiveStatus(active) {
  const response = await psmGet(`/tenant-registry/count/active-status?active=${active}`, "PSM01A");
  return unwrapData(response);
}

/** GET /tenant-industry-types/industries */
export async function getTenantIndustries() {
  const response = await psmGet("/tenant-industry-types/industries");
  return unwrapData(response) || [];
}

/** GET /tenant-industry-types/sectors (enabled sectors for the selected industry) */
export async function getTenantSectors(industryType) {
  const response = await psmGet(
    `/tenant-industry-types/sectors?industryType=${encodeURIComponent(industryType)}&isEnabled=true`
  );
  return unwrapData(response) || [];
}

// ---------------------------------------------------------------------------
// Writes

export const SHARED_DB_HOST = "";
export const DRIVER_CLASS = "org.postgresql.Driver";

function buildTenantPayload(fields) {
  return {
    tenantName: fields.tenantName,
    industryType: fields.industryType || "",
    sectorName: fields.sectorName || "",
    tenantLogo: fields.tenantLogo || "",
    tenantDbUrl: fields.tenantDbUrl,
    dbUsername: fields.dbUsername,
    dbPassword: fields.dbPassword,
    dbSchema: fields.dbSchema,
    driverClass: fields.driverClass,
    isActive: fields.isActive !== undefined ? fields.isActive : true,
    maximumPoolSize: Number(fields.maximumPoolSize),
    minimumIdle: Number(fields.minimumIdle),
    connectionTimeout: Number(fields.connectionTimeout),
    idleTimeout: Number(fields.idleTimeout),
    maxLifetime: Number(fields.maxLifetime),
    leakDetectionThreshold: Number(fields.leakDetectionThreshold),
    subscriptionStartDate: fields.subscriptionStartDate,
    subscriptionEndDate: fields.subscriptionEndDate,
  };
}

/** POST /tenant-registry/add — X-Service-Code: PSM01A */
export async function createTenant(fields) {
  const response = await psmPost("/tenant-registry/add", buildTenantPayload(fields), "PSM01A");
  return unwrapData(response);
}

/** PUT /tenant-registry/update/{tenantId} — X-Service-Code: PSM01U */
export async function updateTenant(tenantId, fields) {
  const response = await psmPut(`/tenant-registry/update/${tenantId}`, buildTenantPayload(fields), "PSM01U");
  return unwrapData(response);
}

/** PATCH /tenant-registry/toggle-status/{tenantId} — X-Service-Code: PSM01U */
export async function toggleTenantStatus(tenantId) {
  const response = await psmPatch(`/tenant-registry/toggle-status/${tenantId}`, {}, "PSM01U");
  return unwrapData(response);
}

// NOTE: no DELETE endpoint exists on the backend yet for tenant-registry.
// Deactivating via toggleTenantStatus() is the closest available action for now.

// NOTE: tenant-service assignment (add/update/search/get-services/delete) is
// owned entirely by ../services/tenantServicesService.js — nothing about
// PSM02* endpoints should live in this file.